let BASE_URL = "https://staging.alertaq.com/api/v4/";

// Handle rate limiting: 30 requests per 2 seconds
const MAX_REQUESTS_PER_WINDOW = 30;
const WINDOW_MS = 2000;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 16000;
const requestTimes = [];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function throttle() {
  for (;;) {
    const now = Date.now();
    while (requestTimes.length && now - requestTimes[0] >= WINDOW_MS) {
      requestTimes.shift();
    }
    if (requestTimes.length < MAX_REQUESTS_PER_WINDOW) {
      requestTimes.push(now);
      return;
    }
    await sleep(WINDOW_MS - (now - requestTimes[0]));
  }
}

async function fetchWithRetry(url, options) {
  let backoff = INITIAL_BACKOFF_MS;
  for (;;) {
    await throttle();
    const response = await fetch(url, options);
    if (response.status !== 429) {
      return response;
    }
    const retryAfter = parseFloat(response.headers.get("Retry-After"));
    const delay = Number.isFinite(retryAfter) ? retryAfter * 1000 : backoff;
    console.warn("[status] WARN: rate limited (429) by " + url + ", retrying in " + delay + "ms");
    await sleep(delay);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  }
}

// Max sensor IDs per getHardwareID call
const HARDWARE_ID_BATCH_SIZE = 500;

var AlertlabsApi = {

    async getSensorDetails(coreId, token) {
        const url = BASE_URL + "dataModel/read";
        const payload = {
            "sensors": {
              "fields": [
                "_id",
                "name",
                "owner",
                "location_id",
                "type",
                "subtype",
                "createdOn",
                "serialNumber",
                "condenser",
                "autoShutOff",
                "autoShutOffSettings",
                "isPairedTo",
                "customAlertName",
                "compound",
                "calibStatus",
                "ancestors",
                "particleSoftwareVersion",
                "alertlabsSoftwareVersion",
                "hardwareVersion",
                "firmwareTimestamp",
                "reportingRate",
                "systemID",
                "unit",
                "defaultUnit",
                "sampleFiltering",
                "samplingSpeed",
                "axis",
                "configurationRequired",
                "firstSeen"
              ],
              "where": {
                "_id": {
                  "$eq": coreId
                }
              }
            }
          }

        const response = await this.postNetworkItem(url, payload, token);
        return response?.dataModel?.[0] ?? null;
    },
    async getLocationInformation(locationId, token) {
        const url = BASE_URL + "dataModel/read";

        const payload = {
            "locations": {
              "fields": [
                "_id",
                "name",
                "street",
                "address",
                
                
              ],
              "where": {
                "_id": {
                  "$eq": locationId
                }
              }
            }
          }

        const response = await this.postNetworkItem(url, payload, token);
        return response?.dataModel?.[0] ?? null;
    },
    async getVirtualSensorID(particleID, token) {
        const url = BASE_URL + "sensors/hardwareIDs/getID";
        const payload = [
            [{name: 'particle', value: particleID}]
        ];

        try {
            const response = await this.postNetworkItem(url, payload, token);
            return response;
        } catch (error) {
            console.warn('[status] WARN: unable to get virtual sensor ID for ' + particleID, error.message);
            return null;
        }
    },
    async getHardwareIDFromSensorID(sensorID, hardwareIDName, token) {
        const url = BASE_URL + "sensors/hardwareIDs/getHardwareID";
        const payload = {
            name: hardwareIDName,
            ids: [sensorID]
        };

        try {
            const response = await this.postNetworkItem(url, payload, token);
            return response;
        } catch (error) {
            console.warn('[status] WARN: unable to get hardware ID from sensor ID ' + sensorID, error.message);
            return null;
        }
    },
    /**
     * Batch version of getHardwareIDFromSensorID: resolves many sensor (virtual) IDs with
     * one getHardwareID call per 500 IDs instead of one call per ID.
     *
     * @returns {Promise<Map<string, string|null>>} sensorID -> hardware ID (null if none)
     */
    async getHardwareIDsFromSensorIDs(sensorIDs, hardwareIDName, token) {
        const url = BASE_URL + "sensors/hardwareIDs/getHardwareID";
        const uniqueIDs = [...new Set(sensorIDs.filter(Boolean))];
        const result = new Map();

        for (let i = 0; i < uniqueIDs.length; i += HARDWARE_ID_BATCH_SIZE) {
            const chunk = uniqueIDs.slice(i, i + HARDWARE_ID_BATCH_SIZE);
            try {
                const response = await this.postNetworkItem(
                    url, { name: hardwareIDName, ids: chunk }, token
                );
                if (!Array.isArray(response)) {
                    console.warn('[status] WARN: unexpected getHardwareID response for ' + chunk.length + ' IDs', response);
                }
                chunk.forEach((id, index) => {
                    const hardwareID = Array.isArray(response) ? response[index] : null;
                    result.set(id, hardwareID || null);
                });
            } catch (error) {
                console.warn('[status] WARN: unable to get hardware IDs for ' + chunk.length + ' sensor IDs', error.message);
                chunk.forEach((id) => result.set(id, null));
            }
        }
        return result;
    },
    async getNetworkItem(url, token) {
        try {
            let response = await fetchWithRetry(url,
                {
                    method: "GET",
                    headers: {
                        "authorization": token
                    }
                });

            if (response.ok) {
                response = response.json();
            } else {
                throw new Error(response.statusText)
            }
            return response;
        } catch (error) {
            console.warn('[status] WARN: unable to retrieve ' + url + ' from the network', error.message);
            throw error;
        }
    },
    async postNetworkItem(url, payload, token) {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("authorization", token);
        headers.append("admin", "true");
        try {
            let response = await fetchWithRetry(url,
                {
                    method: "POST",
                    body: JSON.stringify(payload),
                    headers: headers,
                    // mode: "no-cors",
                    redirect: "follow",
                })

            if (response.ok) {
                response = response.json();
            } else {
                return response.json();
            }
            return response;
        } catch (error) {
            console.warn('[status] WARN: unable to retrieve ' + url + ' from the network', error.message);
            return Promise.reject(error);
        }
    },
    async deleteNetworkItem(url, token) {

        try {
            let response = await fetchWithRetry(url,
                {
                    method: "DELETE",
                    headers: {
                        "authorization": token
                    }
                });

            if (response.ok) {
                response = response.json();
            } else {
                return response.json();
            }
            return response;
        } catch (error) {
            console.warn('[status] WARN: unable to retrieve ' + url + ' from the network', error.message);
            return Promise.reject(error);
        }
    },
}

export default AlertlabsApi;